import React from "react";
import Language from "../lib/Language";
import MassMoveTimeSelect from "./MassMoveTimeSelect";
import MassMoveDateSelect from "./MassMoveDateSelect";
import MassMoveDurationSelect from "./MassMoveDurationSelect";
import BooleanInput from "./BooleanInput";
import { SimpleDateFormat, MillenniumDate } from "@timeedit/millennium-time";

const START_YEAR = 2010;
const END_YEAR = 2040;
const END_MONTH = 12;
const END_DAY = 31;

/**
 * What the dialog reports upwards as the change to apply. MassChangeController
 * keeps it as its changeData and hands it to the mass change itself.
 */
type TMassMoveSelection = {
    /** Days between the first reservation and the date picked, signed. */
    daysToMove: number;
    hours: number;
    minutes: number;
    hoursAndMinutesAreForward: boolean;
    doubleReserveAll: boolean;
    /** Whether the duration below adjusts the current one or replaces it. */
    isDurationRelative: boolean;
    durationHours: number;
    durationMinutes: number;
};

type TMassMoveReservationsDialogProps = {
    /** The earliest reservation's date; every move is measured from it. */
    firstDate: MillenniumDate;
    reservationIds: number[];
    onSelectionChanged: (selection: TMassMoveSelection) => void;
    /** Only changes the wording: copies are moved to a date, not moved. */
    isMassChangeCopy?: boolean;
};

class MassMoveReservationsDialog extends React.Component<
    TMassMoveReservationsDialogProps,
    TMassMoveSelection
> {
    state: TMassMoveSelection = {
        daysToMove: 0,
        hours: 0,
        minutes: 0,
        hoursAndMinutesAreForward: true,
        doubleReserveAll: false,
        isDurationRelative: true,
        durationHours: 0,
        durationMinutes: 0,
    };

    showSelectedDate = (date: MillenniumDate) => {
        this.setState(
            { daysToMove: date.getDayNumber() - this.props.firstDate.getDayNumber() },
            () => this.props.onSelectionChanged(this.state)
        );
    };

    render() {
        const first = SimpleDateFormat.format(
            this.props.firstDate,
            Language.getDateFormat("date_f_yyyy_mm_dd")
        );
        const text = Language.get(
            "nc_mass_move_first_reservation_occurs_at_date_(weekday)",
            first,
            SimpleDateFormat.format(this.props.firstDate, Language.getDateFormat("date_f_eee"))
        );

        return (
            <div>
                <div style={{ display: "flex", flexDirection: "row" }}>
                    <div
                        style={{
                            borderRight: "1px solid grey",
                            marginRight: "40px",
                            paddingRight: "40px",
                        }}
                    >
                        {this._renderTimeSelect()}
                    </div>
                    <div>
                        <div className="massMoveInformation">
                            <p className="headline">
                                {this.props.isMassChangeCopy
                                    ? Language.get("nc_mass_move_select_date_to_move_copies_to")
                                    : Language.get("nc_mass_move_select_date_to_move_to")}
                            </p>
                            <p>{text}</p>
                        </div>
                        {this._renderDateSelect()}
                    </div>
                    <div
                        style={{
                            borderLeft: "1px solid grey",
                            marginLeft: "40px",
                            paddingLeft: "40px",
                        }}
                    >
                        {this._renderDurationSelect()}
                    </div>
                </div>
                <div key="dbBox">
                    <BooleanInput
                        defaultValue={this.state.doubleReserveAll}
                        onUpdate={this.onDoubleReserveAllChanged}
                    />{" "}
                    {Language.get("cal_selected_allow_double_res")}
                </div>
            </div>
        );
    }

    onHoursChanged = (hours) => {
        this.setState({ hours }, () => {
            this.props.onSelectionChanged(this.state);
        });
    };

    onMinutesChanged = (minutes) => {
        this.setState({ minutes }, () => {
            this.props.onSelectionChanged(this.state);
        });
    };

    onTimeDirectionChanged = (event) => {
        this.setState({ hoursAndMinutesAreForward: event.target.value === "true" }, () => {
            this.props.onSelectionChanged(this.state);
        });
    };

    onDoubleReserveAllChanged = (doubleReserveAll) => {
        this.setState({ doubleReserveAll }, () => {
            this.props.onSelectionChanged(this.state);
        });
    };

    onRelativeDurationChanged = (event) => {
        this.setState({ isDurationRelative: event.target.value === "true" }, () => {
            this.props.onSelectionChanged(this.state);
        });
    };

    onDurationHoursChanged = (durationHours) => {
        this.setState({ durationHours }, () => {
            this.props.onSelectionChanged(this.state);
        });
    };

    onDurationMinutesChanged = (durationMinutes) => {
        this.setState({ durationMinutes }, () => {
            this.props.onSelectionChanged(this.state);
        });
    };

    _renderTimeSelect = () => (
        <MassMoveTimeSelect
            hours={this.state.hours}
            minutes={this.state.minutes}
            hoursAndMinutesAreForward={this.state.hoursAndMinutesAreForward}
            onHoursChanged={this.onHoursChanged}
            onMinutesChanged={this.onMinutesChanged}
            onDirectionChanged={this.onTimeDirectionChanged}
        />
    );

    _renderDateSelect = () => (
        <MassMoveDateSelect
            showSelectedDate={this.showSelectedDate}
            weekday={this.props.firstDate.getDay()}
            startDate={MillenniumDate.create(START_YEAR, 1, 1)}
            endDate={MillenniumDate.create(END_YEAR, END_MONTH, END_DAY)}
            now={this.props.firstDate}
            highlightDates={[this.props.firstDate]}
            isMassChangeCopy={this.props.isMassChangeCopy}
        />
    );

    _renderDurationSelect = () => (
        <MassMoveDurationSelect
            numberOfReservations={this.props.reservationIds.length}
            isDurationRelative={this.state.isDurationRelative}
            onRelativeDurationChanged={this.onRelativeDurationChanged}
            hours={this.state.durationHours}
            minutes={this.state.durationMinutes}
            onHoursChanged={this.onDurationHoursChanged}
            onMinutesChanged={this.onDurationMinutesChanged}
        />
    );
}

export default MassMoveReservationsDialog;
